import { useMutation, useQuery } from '@apollo/client';
import { MoreVert, NavigateNext, NotificationsActiveOutlined, ShoppingBagOutlined } from '@mui/icons-material';
import { Breadcrumbs, Button, IconButton, Link, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { Restricted, usePermissions } from '@procureai/permissions-ui';
import { useButtonMenu, useConfirmationDialog, WidgetIcon, useCustomSnackbar } from '@procureai/ui-mui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@procureai/uix';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  DELETE_SOURCING_CASE_MUTATION,
  RELEASE_SOURCING_CASE_MUTATION,
  REMIND_SOURCING_CASE_MUTATION,
  RESET_SOURCING_CASE_MUTATION,
  SOURCING_CASE_DETAIL_QUERY,
} from '../SourcingCaseDetail.gql';
import {
  TlscpEntityVariant,
  TsrctAttachmentParentVariant,
  TsrctCommentParentVariant,
  TsrctSourcingCaseStatus,
  TsrctUploadedByVariant,
} from '@/generated/gql/graphql';
import { CommentsPopoverButton } from '@/legacy/components/CommentsPopoverButton/CommentsPopoverButton';
import { SourcingCaseCancelDialog } from '@/legacy/components/SourcingCaseCancelDialog/SourcingCaseCancelDialog';
import { SupplierHeading } from '@/legacy/components/SupplierHeading/SupplierHeading';
import { SOURCING_CASE_SUPPLIER_FILE_ATTACHMENTS_QUERY } from '@/legacy/modules/contractDemands/SourcingCaseSupplierFileAttachmentsWidget/SourcingCaseSupplierFileAttachmentsWidget.gql';
import { ModuleTabOptions } from '~/components/ModulePageConfigProvider/ModulePageConfigProvider.types';
import { ModulePageHeader } from '~/components/ModulePageHeader/ModulePageHeader';
import { SourcingCaseStatusChip } from '~/components/SourcingCaseStatusChip/SourcingCaseStatusChip';
import { BusinessLogPopoverButton } from '~/modules/BusinessLogWidget/components/BusinessLogPopoverButton/BusinessLogPopoverButton';
import { CONTRACT_DEMAND_CASES_QUERY } from '~/modules/contractDemands/ContractDemandCaseListWidget/ContractDemandCaseListWidget.gql';
import { RouteId } from '~/routes/RouteId';

const REMIND_ENABLED_STATES: { [key in TsrctSourcingCaseStatus]?: true } = {
  [TsrctSourcingCaseStatus.RELEASED]: true,
  [TsrctSourcingCaseStatus.SUPPLIER_AGREEMENT_REACHED]: true,
};

type SourcingCaseOverFlowMenuProps = {
  id: string;
  demandId: string;
  status?: TsrctSourcingCaseStatus;
};
export const SourcingCaseOverFlowMenu = ({ id, demandId, status }: SourcingCaseOverFlowMenuProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SourcingCaseDetail' });
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { enqueueSuccessSnackbar, onError } = useCustomSnackbar();

  const [deleteCase, { loading: deleteLoading }] = useMutation(DELETE_SOURCING_CASE_MUTATION, {
    onError,
    refetchQueries: [CONTRACT_DEMAND_CASES_QUERY],
  });
  const [resetCase, { loading: resetLoading }] = useMutation(RESET_SOURCING_CASE_MUTATION, {
    onError,
    refetchQueries: [
      {
        query: SOURCING_CASE_SUPPLIER_FILE_ATTACHMENTS_QUERY,
        variables: {
          parents: [{ variant: TsrctAttachmentParentVariant.SOURCING_CASE, id }],
          filter: {
            uploadedByVariants: [TsrctUploadedByVariant.SUPPLIER_USER],
          },
          sourcingCaseId: id,
        },
      },
    ],
  });
  const [remindCase, { loading: remindLoading }] = useMutation(REMIND_SOURCING_CASE_MUTATION, { onError });
  const { menuProps, buttonProps } = useButtonMenu();
  const { confirm } = useConfirmationDialog();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const handleDelete = async () => {
    const { errors } = await deleteCase({ variables: { id } });
    if (!errors) {
      enqueueSuccessSnackbar(t('action.delete.success'));
      navigate(RouteId.contractDemandDetail(demandId));
    }
  };

  const handleReset = async () => {
    const { errors } = await resetCase({ variables: { id } });
    if (!errors) {
      enqueueSuccessSnackbar(t('action.reset.success'));
    }
  };

  const handleRemind = async () => {
    const { errors } = await remindCase({ variables: { id } });
    if (!errors) {
      enqueueSuccessSnackbar(t('action.remind.success'));
    }
  };

  return (
    <div>
      <IconButton
        {...buttonProps}
        disabled={
          !hasPermission({
            any: [
              Permission.SourcingCaseReset,
              Permission.SourcingCaseDelete,
              Permission.SourcingCaseRelease,
              Permission.SourcingCaseCancel,
            ],
          })
        }
        loading={deleteLoading || resetLoading || remindLoading}
      >
        <MoreVert />
      </IconButton>
      <Menu {...menuProps}>
        <MenuItem disabled={status ? !REMIND_ENABLED_STATES[status] : true} onClick={handleRemind}>
          <NotificationsActiveOutlined fontSize="small" sx={{ mr: 1 }} />
          {t('action.remind.label')}
        </MenuItem>
        <MenuItem
          disabled={!hasPermission(Permission.SourcingCaseCancel) || status === TsrctSourcingCaseStatus.CANCELLED}
          onClick={() => setCancelDialogOpen(true)}
        >
          {t('action.cancel.label')}
        </MenuItem>
        <MenuItem
          disabled={!hasPermission(Permission.SourcingCaseReset)}
          onClick={confirm(handleReset, { message: t('action.reset.confirm') })}
        >
          {t('action.reset.label')}
        </MenuItem>
        <MenuItem
          disabled={!hasPermission(Permission.SourcingCaseDelete)}
          onClick={confirm(handleDelete, { message: t('action.delete.confirm') })}
        >
          {t('action.delete.label')}
        </MenuItem>
      </Menu>
      <SourcingCaseCancelDialog
        sourcingCaseId={id}
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      />
    </div>
  );
};

export const SourcingCaseDetailHeader = ({
  id,
  tabs,
}: {
  id: string;

  tabs: ModuleTabOptions[] | undefined;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SourcingCaseDetail' });
  const { enqueueSuccessSnackbar, onError } = useCustomSnackbar();
  const { data, loading } = useQuery(SOURCING_CASE_DETAIL_QUERY, {
    variables: {
      id,
    },
  });

  const sourcingCase = data?.tesseract.sourcingCase;
  const supplier = sourcingCase?.supplier;
  const sequenceNumber = sourcingCase?.sequenceNumber;
  const demandSequenceNumber = sourcingCase?.demand?.sequenceNumber;

  const isReleasable = sourcingCase?.status === TsrctSourcingCaseStatus.RELEASABLE;

  const [releaseSourcingCase, { loading: isReleasing }] = useMutation(RELEASE_SOURCING_CASE_MUTATION, {
    variables: { id },
    refetchQueries: [{ query: SOURCING_CASE_DETAIL_QUERY, variables: { id } }],
    onError,
  });

  const handleRelease = async () => {
    const { errors } = await releaseSourcingCase();
    if (!errors) {
      enqueueSuccessSnackbar(t('action.release.success'));
    }
  };

  return (
    <ModulePageHeader
      title={
        !loading && sourcingCase ? (
          <Breadcrumbs aria-label="contract-demand-navigation" separator={<NavigateNext fontSize="small" />}>
            <Restricted needs={{ permission: Permission.DemandList }}>
              <Link
                variant="h6"
                color="text.primary"
                underline="hover"
                component={RouterLink}
                to={RouteId.contractDemandDetail(sourcingCase?.demand?.id)}
                data-testid="SourcingCaseDetail/Header/ContractDemand"
              >
                {t('header.demand', {
                  sequenceNumber: demandSequenceNumber,
                })}
              </Link>
            </Restricted>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" color="text.primary">
                {t('header.case', { sequenceNumber })}
              </Typography>
              <SourcingCaseStatusChip status={sourcingCase?.status} />
            </Stack>
          </Breadcrumbs>
        ) : null
      }
      icon={<WidgetIcon icon={<ShoppingBagOutlined fontSize="small" />} />}
      tabs={tabs}
      center={
        <Stack
          spacing={3}
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          <SupplierHeading supplierId={supplier?.supplierId} />

          <Restricted needs={{ permission: Permission.CaseRelease }}>
            {isReleasable ? (
              <Button loading={isReleasing} variant="contained" color="primary" onClick={handleRelease}>
                {t('action.release.label')}
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span className="inline-flex cursor-help">
                      <Button disabled loading={isReleasing} variant="contained" color="primary">
                        {t('action.release.label')}
                      </Button>
                    </span>
                  }
                />
                <TooltipContent>{t('action.release.disabledTooltip')}</TooltipContent>
              </Tooltip>
            )}
          </Restricted>
        </Stack>
      }
      right={
        <Stack
          spacing={3}
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          {!loading && sourcingCase && (
            <>
              <CommentsPopoverButton
                entityId={sourcingCase.id}
                entityVariant={TsrctCommentParentVariant.SOURCING_CASE}
              />
              <BusinessLogPopoverButton entityId={sourcingCase.id} entityVariant={TlscpEntityVariant.SOURCING_CASE} />
            </>
          )}
          {sourcingCase && sourcingCase.demand && (
            <SourcingCaseOverFlowMenu
              id={sourcingCase.id}
              demandId={sourcingCase.demand.id}
              status={sourcingCase.status}
            />
          )}
        </Stack>
      }
    />
  );
};
